import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { ConfirmationService } from 'primeng/api';
import { Router } from '@angular/router';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { UntypedFormBuilder, UntypedFormControl, Validators, FormGroup } from '@angular/forms';
import { CustomerModel } from 'projects/core/src/lib/models/customer.model';
import { BaseForm } from 'projects/admin/src/app/classes/base-form';
import { Component, OnChanges, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { CountryModel } from 'projects/core/src/lib/models/country.model';
import { HelperService } from 'projects/admin/src/app/services/helper.service';
import { CustomValidators } from 'projects/core/src/lib/validators/custom.validators';
import { parsePhoneNumber } from 'libphonenumber-js';

@Component({
  selector: 'app-customer-basedata',
  templateUrl: './customer-basedata.component.html',
  styleUrls: ['./customer-basedata.component.scss']
})
export class CustomerBasedataComponent extends BaseForm implements OnInit, OnChanges {

  @Input() customer: CustomerModel = null;
  @Output() setEdit = new EventEmitter<string>();

  public countries: Array<CountryModel> = [];

  public form = this.fb.group({
    number: new UntypedFormControl(''),
    name: new UntypedFormControl('', [Validators.required]),
    company: new UntypedFormControl(''),
    street: new UntypedFormControl(''),
    zip: new UntypedFormControl(''),
    city: new UntypedFormControl(''),
    country: new UntypedFormControl(''),
    phone: new UntypedFormControl('', [CustomValidators.phone()]),
    email: new UntypedFormControl('', [CustomValidators.email()]),
    web: new UntypedFormControl('', [CustomValidators.url()]),
    informations: new UntypedFormControl(''),
  });

  constructor(
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private api: ApiService,
    private router: Router,
    private auth: AuthStateService,
    private confirmation: ConfirmationService,
    public transloco: TranslocoService,
    private helper: HelperService
  ) {
    super();
  }

  ngOnInit() {
    if (this.auth.checkRightOrRedirect('admin.customer.edit')) {
      this.loading = true;
      this.api.getModels('countries').subscribe({
        next: (result: any) => {
          this.countries = this.helper.getCountryOptions(result.data);
        }
      }).add(() => {
        this.loading = false;
      });
      this.ui.setFormFocus();
    }
  }

  ngOnChanges(): void {
    this.items = [];
    if (this.customer !== null) {
      if (this.auth.hasRight('admin.customer.delete')) {
        this.items.push({
          label: this.transloco.translate('common.labels.delete'),
          icon: 'pi pi-trash',
          styleClass: 'danger-button',
          disabled: (!this.customer.deletable),
          command: () => {
            this.onDelete();
          }
        });
      }
    }
  }

  public onSubmit(): void {
    this.ui.hideMessage();
    if (this.form.valid === true) {
      this.submitting = true;
      const payload = this.form.value;
      payload.country = payload.country?.country_code || null;
      const phone = (this.form.value.phone) ? parsePhoneNumber(this.form.value.phone, payload.country || 'DE') : null;
      payload.phone = phone?.formatInternational() || null;
      this.api.saveModel('customers', this.customer.id, payload).subscribe({
        next: (result: any) => {
          this.customer.deserialize(result.data);
          this.ui.toastSuccess(this.transloco.translate('common.messages.saved', { value: this.customer.name }), this.transloco.translate('common.saved'));
          this.stopEdit();
        },
        error: (error: any) => {
          this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
          FormHelper.setBackendErrors(this.form, error.fielderrors);
        }
      }).add(() => {
        this.submitting = false;
      });
    } else {
      this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
      FormHelper.markFormAsDirty(this.form);
    }
  }

  public onDelete(): void {
    if (this.auth.hasRight('admin.customer.delete')) {
      this.confirmation.confirm({
        message: this.transloco.translate('common.messages.really_delete', { value: this.customer.name }),
        header: this.transloco.translate('common.really_delete'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.api.deleteModel('customers', this.customer.id).subscribe({
            next: () => {
              this.ui.toastSuccess(this.transloco.translate('common.messages.deleted', { value: this.customer.name }), this.transloco.translate('common.deleted'));
              this.router.navigate(['/customers']);
            },
            error: (error: any) => {
              this.ui.toastError(this.transloco.translate(error.message));
            }
          });
        }
      });
    }
  }

  public getCountryFromCode(code: string): string {
    const country = this.countries.find(c => c.country_code === code);
    return country ? country.country_name : '';

  }

  protected startEdit(): void {
    if (this.customer !== null) {
      this.form.patchValue({
        number: this.customer.number,
        name: this.customer.name,
        company: this.customer.company,
        street: this.customer.street,
        zip: this.customer.zip,
        city: this.customer.city,
        country: this.countries.find(country => country.country_code === this.customer.country) || null,
        phone: this.customer.phone,
        email: this.customer.email,
        web: this.customer.web,
        informations: this.customer.informations,
      });
    }
    this.setEdit.emit('basedata');
    super.startEdit();
    this.ui.setFormFocus();
  }

  protected stopEdit(): void {
    this.setEdit.emit(null);
    super.stopEdit();
    this.ui.setFocus();
  }

}
